import { enableProdMode, importProvidersFrom } from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { PortalModule } from '@angular/cdk/portal';
import { PlatformModule } from '@angular/cdk/platform';
import { OverlayModule } from '@angular/cdk/overlay';
import { ObserversModule } from '@angular/cdk/observers';
import { BidiModule } from '@angular/cdk/bidi';
import { A11yModule } from '@angular/cdk/a11y';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { LowerCaseUrlSerializer } from './app/tools/lower.case.url.serializer';
import {
  PreloadAllModules,
  provideRouter,
  UrlSerializer,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { DatePipe, CommonModule } from '@angular/common';
import { ApiService } from './app/service/api.service';
import { ConfirmMessageDialogService } from './app/components/shared/confirm-message-dialog.service';
import { CanDeactivateGuard } from './app/tools/can-deactivate-guard.service';
import { ResizeService } from './app/service/screen.resize.service';
import { SharedFunctionService } from './app/service/shared.function.service';
import { MatStepperIntl, MatStepperModule } from '@angular/material/stepper';
import { GroupMembersService } from './app/service/group-members.service';
import { DataLoader } from './app/service/data.loader';
import { DataLoaderService } from './app/service/data.loader.service';
import { LoginService } from './app/service/login.service';
import { SysConfigService } from './app/service/sys.config';
import { ApiAddressService } from './app/service/api.address.service';
import { TINYMCE_SCRIPT_SRC, EditorModule } from '@tinymce/tinymce-angular';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CommonModule,
      FormsModule,
      BrowserModule,
      ReactiveFormsModule,
      MatAutocompleteModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatCheckboxModule,
      MatChipsModule,
      MatDatepickerModule,
      MatDialogModule,
      MatExpansionModule,
      MatFormFieldModule,
      MatGridListModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatMenuModule,
      MatNativeDateModule,
      MatPaginatorModule,
      MatProgressBarModule,
      MatProgressSpinnerModule,
      MatRadioModule,
      MatRippleModule,
      MatSelectModule,
      MatSidenavModule,
      MatSliderModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatSortModule,
      MatTableModule,
      MatTabsModule,
      MatToolbarModule,
      MatTooltipModule,
      MatBadgeModule,
      MatStepperModule,
      A11yModule,
      BidiModule,
      ObserversModule,
      OverlayModule,
      PlatformModule,
      PortalModule,
      MatBottomSheetModule,
      DragDropModule,
      MatSidenavModule,
      EditorModule
    ),
    ApiAddressService,
    SysConfigService,
    LoginService,
    DataLoaderService,
    DataLoader,
    GroupMembersService,
    MatStepperIntl,
    SharedFunctionService,
    ResizeService,
    CanDeactivateGuard,
    ConfirmMessageDialogService,
    ApiService,
    DatePipe,
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-NZ',
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'tinymce/tinymce.min.js',
    },
    provideRouter(
      routes,
      // Preloading Strategy
      withPreloading(PreloadAllModules),
      // Initial Navigation (for SSR)
      withEnabledBlockingInitialNavigation(),
      // Scrolling Behavior (replaces scrollPositionRestoration and anchorScrolling)
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      })
      // For `useHash: true`, add `withHashLocation()`. The default behavior is to use the PathLocationStrategy (clean URLs). Only add withHashLocation() if want useHash: true.
      // For `enableTracing: true`, add `withDebugTracing()`. The default is not to trace. Only add the withDebugTracing() function when need to debug router events.
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
